import React, { useEffect, useState } from "react";
import { MapPin, Star, Briefcase, Check } from "lucide-react";
import { api } from "@/lib/api";
import { useLang } from "@/context/LangContext";

export const JobCatalog = ({ onApply }) => {
  const { t } = useLang();
  const [jobs, setJobs] = useState([]);
  const [q, setQ] = useState("");

  useEffect(() => {
    api.get("/jobs").then((r) => setJobs(r.data)).catch(() => {});
  }, []);

  const filtered = jobs.filter(
    (j) =>
      j.title.toLowerCase().includes(q.toLowerCase()) ||
      j.location.toLowerCase().includes(q.toLowerCase()) ||
      j.category.toLowerCase().includes(q.toLowerCase())
  );

  return (
    <section id="vacantes" data-testid="job-catalog-section" className="py-16 md:py-28 bg-[#F5F7FB]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-10">
          <div>
            <span className="text-[#E63946] text-sm font-semibold uppercase tracking-wider">{t("jobs.eyebrow")}</span>
            <h2 className="font-head font-extrabold text-3xl md:text-5xl mt-2 text-[#0B132B]">{t("jobs.title")}</h2>
            <div className="ej-accent-bar mt-4" />
          </div>
          <input
            data-testid="job-search-input"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder={t("jobs.search")}
            className="bg-white border border-slate-300 rounded-full px-5 py-3 text-sm w-full md:w-80 focus:outline-none focus:border-[#FFD166] text-[#0B132B] placeholder:text-slate-400"
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filtered.map((j) => (
            <div
              key={j.id}
              data-testid={`job-card-${j.id}`}
              className="bg-white rounded-2xl border border-slate-200 hover:border-[#FFD166] card-hover shadow-sm p-6 flex flex-col"
            >
              <div className="flex items-center justify-between">
                <span className="bg-[#FFF6DC] text-[#B8860B] border border-[#FFD166]/60 px-3 py-1 rounded-full text-xs font-semibold uppercase tracking-wider">
                  {j.category}
                </span>
                {j.featured && (
                  <span className="flex items-center gap-1 text-[#E63946] text-xs font-bold"><Star size={13} fill="currentColor" /> {t("jobs.featured")}</span>
                )}
              </div>
              <h3 className="font-head font-bold text-xl mt-4 text-[#0B132B]">{j.title}</h3>
              <div className="flex items-center gap-2 text-slate-500 text-sm mt-2">
                <Briefcase size={14} /> {j.company}
              </div>
              <div className="flex items-center gap-2 text-slate-500 text-sm mt-1">
                <MapPin size={14} /> {j.location}, {j.country}
              </div>
              <div className="text-2xl font-head font-extrabold text-[#16a34a] mt-4">{j.salary}</div>
              <p className="text-slate-600 text-sm mt-3 line-clamp-3 flex-1">{j.description}</p>
              {j.benefits?.length > 0 && (
                <div className="mt-4 space-y-1.5">
                  {j.benefits.slice(0, 3).map((b) => (
                    <div key={b} className="flex items-center gap-2 text-xs text-slate-600">
                      <Check size={14} className="text-[#16a34a]" /> {b}
                    </div>
                  ))}
                </div>
              )}
              <button
                onClick={() => onApply(j)}
                data-testid={`job-apply-${j.id}`}
                className="mt-5 w-full bg-[#0B132B] text-white font-bold py-3 rounded-full hover:bg-[#1C2541] transition-colors"
              >
                {t("jobs.apply")}
              </button>
            </div>
          ))}
        </div>
        {filtered.length === 0 && (
          <div className="text-center text-slate-500 py-16">{t("jobs.none")}</div>
        )}
      </div>
    </section>
  );
};
